import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePrefixedSearchParams } from '@shared-ui/navigation';
import { formatDistanceToNow } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import { authApi } from '../../api/auth';
import {
  DataTable,
  SearchFilterBar,
  type ColumnDef,
  type SortState,
  type FilterDef,
  formatDateShort,
  Button, TabStrip } from '@shared-ui/components';
import type { User } from '../../types';
import { ALL_TENANTS, useSelectedTenantId, hasScopedTenant } from '../../components/TenantFilter';
import { isTenantAdmin } from '../../utils/userRoles';
import { PUBLIC_TENANT_ID } from '../../api/tenantsApi';
import { operatesAcrossTenants } from '../../config/adminRoles';
import { acquisitionSource, describeAcquisition } from '../../utils/acquisition';
import { InviteUserModal } from './InviteUserModal';
import './Users.css';

const PAGE_SIZE = 20;

function formatLastActive(dateStr?: string): string {
  if (!dateStr) return 'Never';
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return 'Unknown';
  }
}

function getSubjectsLabel(user: User): string {
  if (user.roles?.includes('teacher')) {
    return user.teacherSubjects?.join(', ') || '-';
  }
  return user.subjects?.join(', ') || '-';
}

function getTeachersLabel(user: User): string {
  return user.teachers?.join(', ') || '-';
}

function getSubscriptionLabel(user: User): string {
  if (user.bypassSubscription) return 'Bypass';
  if (!user.subscriptions || user.subscriptions.length === 0) return '-';
  const names = [...new Set(user.subscriptions.map(s => s.subscriptionName))];
  return names.join(', ');
}

// ── Column definitions per section ──────────────────────────────────────

function useBaseColumns(showTenant: boolean, showRing: boolean): ColumnDef<User>[] {
  return [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      width: '15%',
      hideable: false,   // primary identity column — always shown
      // The whole row navigates (see UserTabPanel) — the name only carries the
      // link styling that says so.
      render: (user) => (
        <>
          <span className="dc-name-link">{user.name}</span>
          {user.deletedAt && <span className="dc-status dc-status--deleted">Deleted</span>}
          {!user.deletedAt && user.invitedBy && !user.lastActivityAt && <span className="dc-status dc-status--invited">Invited</span>}
        </>
      ),
    },
    {
      // One column, not two. For a self-registered user the email IS the login
      // id, so two columns printed the same address twice on every row. A
      // roster account (`1042.teoler`) has a handle and no email, so the handle
      // stands in — which is the only case the second column ever added.
      //
      // Sorts by loginId, which every user has; sorting by email would sink the
      // roster accounts to one end regardless of what they are called.
      key: 'loginId',
      header: 'Email',
      sortable: true,
      width: '18%',
      className: 'users-cell--ellipsis',
      render: (user) => (user.email
        ? <span className="users-email">{user.email}</span>
        : user.loginId
          ? <span className="users-login-id" title="Roster account — signs in with this handle, no email">{user.loginId}</span>
          : <span className="dc-empty">—</span>),
    },
    // Ring is how WE roll a release out — devtesting, beta, prod. A customer's
    // admin neither sets it nor can act on it.
    ...(showRing ? [{
      key: 'ring',
      header: 'Ring',
      width: '7%',
      render: (user: User) => {
        const r = user.ring || 'prod';
        return <span className={`dc-pill dc-pill--${r}`}>{r}</span>;
      },
    } satisfies ColumnDef<User>] : []),
    // Tenant column shown only to super-admin viewing "All tenants". When a
    // specific tenant is scoped (or for tenant-admins, who are server-locked
    // to their own tenant), every row would carry the same tenant — column
    // is redundant noise.
    ...(showTenant ? [{
      key: 'tenantId',
      header: 'Tenant',
      sortable: true,
      width: '8%',
      render: (user: User) => {
        const tid = user.tenantId;
        if (!tid || tid === '0') {
          return <span className="dc-pill dc-pill--tenant-public">Public</span>;
        }
        return <code className="dc-tenant-id">{tid}</code>;
      },
    } satisfies ColumnDef<User>] : []),
    {
      key: 'lastActivityAt',
      header: 'Last Active',
      sortable: true,
      width: '10%',
      render: (user) => <span className="dc-time-ago">{formatLastActive(user.lastActivityAt)}</span>,
    },
    {
      key: 'createdAt',
      header: 'Joined',
      sortable: true,
      width: '7%',
      render: (user) => <span className="dc-date">{formatDateShort(user.createdAt)}</span>,
    },
    // Where the account came from — the most specific thing its touch knows:
    // a campaign source, a referrer, a link, or how it was provisioned.
    // Hidden by default; the row already carries plenty, and the Acquisition
    // tab is where this is counted rather than read one at a time.
    {
      key: 'source',
      header: 'Source',
      width: '9%',
      defaultHidden: true,
      render: (user) => user.acquisition
        ? <span className="dc-list" title={describeAcquisition(user.acquisition)}>{acquisitionSource(user.acquisition)}</span>
        : <span className="dc-empty">—</span>,
    },
  ];
}

function getStudentColumns(showTenant: boolean, showTeachers: boolean): ColumnDef<User>[] {
  return [
    // Ring is platform machinery; a customer's admin never sees it. Public
    // tenant and super-admin are the only viewers for whom it means anything,
    // and both are exactly the viewers who see Teachers.
    ...useBaseColumns(showTenant, showTeachers),
    /* Grade and Section describe a ROOM, and only a school has rooms. In the
       public tenant a student picks their own tutors and sits in no section —
       so Section was a column of dashes there, and Grade is on the profile
       rather than a placement. The two columns and Teachers are alternatives:
       a school's students are taught by whoever teaches their section, so
       naming the teachers per student prints the same three names down the
       whole grade; the public tenant has no section, so the teachers ARE the
       placement. `showTeachers` is true exactly when the data on screen is the
       public tenant's. */
    ...(!showTeachers ? [
      { key: 'grade', header: 'Grade', sortable: true, width: '6%', render: (user: User) => (user.grade ? String(user.grade) : <span className="dc-empty">—</span>) },
      // Sorts the loaded page only — see the note on Operations below; a section
      // is not a field on the user doc, so there is nothing for the server to
      // ORDER BY.
      { key: 'section', header: 'Section', width: '8%', render: (user: User) => user.section ?? <span className="dc-empty">—</span> },
    ] satisfies ColumnDef<User>[] : []),
    { key: 'subscriptions', header: 'Subscription', width: '9%', render: (user) => <span className="dc-list">{getSubscriptionLabel(user)}</span> },
    { key: 'subjects', header: 'Subjects', width: '18%', render: (user) => <span className="dc-list">{getSubjectsLabel(user)}</span> },
    ...(showTeachers ? [{
      key: 'teachers', header: 'Teachers', width: '14%',
      render: (user: User) => <span className="dc-list">{getTeachersLabel(user)}</span>,
    } satisfies ColumnDef<User>] : []),
    // Sorted client-side (see UserTabPanel): operationCount isn't a stored
    // user field — it's aggregated per page — so a server ORDER BY isn't
    // possible without a full-container scan. Sorts the loaded page only.
    { key: 'operationCount', header: 'Operations', sortable: true, width: '7%', render: (user) => <span className="dc-count">{user.operationCount || 0}</span> },
  ];
}

function getTeacherColumns(showTenant: boolean, isPlatformViewer: boolean): ColumnDef<User>[] {
  return [
    ...useBaseColumns(showTenant, isPlatformViewer),
    { key: 'teacherSubjects', header: 'Subjects', render: (user) => <span className="dc-list">{getSubjectsLabel(user)}</span> },
    { key: 'studentCount', header: '# Students', render: (user) => <span className="dc-count">{user.studentCount || 0}</span> },
    { key: 'activeStudentCount7d', header: 'Active (7d)', render: (user) => <span className="dc-count">{user.activeStudentCount7d || 0}</span> },
  ];
}

function getAdminColumns(showTenant: boolean, isPlatformViewer: boolean): ColumnDef<User>[] {
  return [
    ...useBaseColumns(showTenant, isPlatformViewer).filter(c => c.key !== 'lastActivityAt'),
    {
      key: 'roles',
      header: 'Roles',
      render: (user) => (user.roles && user.roles.length > 0
        ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{user.roles.join(', ')}</span>
        : <span className="dc-empty">—</span>),
    },
    {
      key: 'invitedByName',
      header: 'Created by',
      render: (user) => user.invitedByName
        ? <span>{user.invitedByName}</span>
        : <span className="dc-empty">—</span>,
    },
  ];
}

// ── Per-tab table with its own search, pagination & sort ────────────

interface FilterConfig {
  ring?: boolean;
  teacher?: boolean;
  dummy?: boolean;
}

const RING_OPTIONS = [
  { value: 'prod', label: 'prod' },
  { value: 'beta', label: 'beta' },
  { value: 'devtesting', label: 'devtesting' },
  { value: 'guest', label: 'guest' },
];
// Synthetic populations hidden by default (show-set): check to add them in.
const SYNTHETIC_OPTIONS = [
  { value: 'dummies', label: 'Dummies' },
  { value: 'agents', label: 'Agents' },
];

interface TabPanelProps {
  role: string;
  columns: ColumnDef<User>[];
  showDeleted: boolean;
  defaultSort?: SortState;
  filters?: FilterConfig;
  teachers?: Array<{ id: string; name: string }>;
}

function UserTabPanel({ role, columns, showDeleted, defaultSort, filters, teachers }: TabPanelProps) {
  const navigate = useNavigate();
  // Page / sort / applied filters live in the URL, namespaced by role so the
  // student, teacher and admin tabs don't bleed params into each other. See
  // docs/FeatureDesigns/back-navigation-context.md.
  const url = usePrefixedSearchParams(role);
  const APPLIED_KEYS = ['search', 'ring', 'teacher', 'synthetic'] as const;
  const page = Number(url.get('page') || '1') || 1;
  const setPage = (p: number) => url.set({ page: p > 1 ? p : '' });
  const sortRaw = url.get('sort');
  const sort: SortState = sortRaw
    ? { sortBy: sortRaw.split(':')[0], sortOrder: (sortRaw.split(':')[1] === 'asc' ? 'asc' : 'desc') }
    : (defaultSort ?? { sortBy: 'createdAt', sortOrder: 'desc' });
  const setSort = (nextSort: SortState) => url.set({ sort: `${nextSort.sortBy}:${nextSort.sortOrder}` });
  // Committed filter values — updated only when the user clicks Fetch in the
  // SearchFilterBar (drafts live inside the bar until then, so the list
  // refreshes on Fetch, not on every keystroke/selection). Keys: `search`,
  // `ring` (comma-joined list), `teacher`, `synthetic` (comma list of
  // 'dummies' / 'agents'). Pagination and sorting stay live (not gated by Fetch).
  const applied = url.getAll(APPLIED_KEYS);
  const setApplied = (values: Record<string, string>) =>
    url.set(Object.fromEntries(APPLIED_KEYS.map(k => [k, values[k] ?? ''])));

  // Sidebar TenantFilter — `ALL_TENANTS` means don't scope. For tenant-admin
  // the backend ignores the param and force-scopes to their own tenant.
  const selectedTenantId = useSelectedTenantId();
  const tenantScope = selectedTenantId === ALL_TENANTS ? undefined : selectedTenantId;

  // Reset page when showDeleted or tenant scope changes.
  const [prevShowDeleted, setPrevShowDeleted] = useState(showDeleted);
  if (showDeleted !== prevShowDeleted) {
    setPrevShowDeleted(showDeleted);
    if (page !== 1) setPage(1);
  }
  const [prevTenantScope, setPrevTenantScope] = useState(tenantScope);
  if (tenantScope !== prevTenantScope) {
    setPrevTenantScope(tenantScope);
    if (page !== 1) setPage(1);
  }

  const search = applied.search || '';
  const rings = filters?.ring ? (applied.ring || '') : '';
  const teacherId = filters?.teacher ? (applied.teacher || '') : '';
  const synthetic = filters?.dummy && applied.synthetic ? applied.synthetic.split(',') : [];
  const includeDummies = filters?.dummy ? synthetic.includes('dummies') : false;
  const includeAgents = filters?.dummy ? synthetic.includes('agents') : false;

  const { data, isLoading, isPlaceholderData } = useQuery({
    queryKey: ['users', role, { page, pageSize: PAGE_SIZE, search, ...sort, includeDeleted: showDeleted, rings, teacherId, tenantId: tenantScope, includeDummies, includeAgents }],
    queryFn: () => adminApi.getUsers({
      page,
      pageSize: PAGE_SIZE,
      search: search || undefined,
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
      role,
      includeDeleted: showDeleted,
      rings: rings || undefined,
      teacherId: teacherId || undefined,
      tenantId: tenantScope,
      includeDummies: includeDummies || undefined,
      includeAgents: includeAgents || undefined,
    }),
    placeholderData: (prev) => prev,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  // `operationCount` has no stored user field to ORDER BY server-side (the
  // count is aggregated per page), so a global sort would need a full-container
  // scan. It's sorted client-side over the loaded page; every other column
  // sorts server-side (across pages) via the query's sortBy.
  const rows = useMemo(() => {
    const items = data?.items ?? [];
    if (sort.sortBy !== 'operationCount') return items;
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => ((a.operationCount || 0) - (b.operationCount || 0)) * dir);
  }, [data, sort]);

  // Toast shown when sorting by the client-only Submissions column, so the
  // operator knows it reorders the current page rather than all pages.
  const [pageSortToast, setPageSortToast] = useState(false);
  useEffect(() => {
    if (!pageSortToast) return;
    const t = setTimeout(() => setPageSortToast(false), 5000);
    return () => clearTimeout(t);
  }, [pageSortToast]);

  const handleSortChange = (newSort: SortState) => {
    setSort(newSort);
    setPage(1);
    setPageSortToast(newSort.sortBy === 'operationCount');
  };

  // Fires when the SearchFilterBar's Fetch button is clicked (or search Enter).
  const handleFiltersChange = (values: Record<string, string>) => {
    setApplied(values);
    setPage(1);
  };

  const filterDefs: FilterDef[] = [];
  if (filters?.ring) {
    filterDefs.push({ key: 'ring', kind: 'multiselect', placeholder: 'All Rings', options: RING_OPTIONS });
  }
  if (filters?.teacher && teachers && teachers.length > 0) {
    filterDefs.push({ key: 'teacher', kind: 'select', placeholder: 'All Teachers', options: teachers.map(t => ({ value: t.id, label: t.name })) });
  }
  if (filters?.dummy) {
    filterDefs.push({ key: 'synthetic', kind: 'multiselect', placeholder: 'Real users only', options: SYNTHETIC_OPTIONS });
  }

  return (
    <>
      <SearchFilterBar
        filters={filterDefs}
        values={applied}
        onChange={handleFiltersChange}
        searchPlaceholder="Search name or login id..."
      />
      <DataTable
        columns={columns}
        data={rows}
        rowKey={(user) => user.id}
        // Carry the list's current query (filters/sort/page) so the detail
        // page's Back returns to the same filtered view.
        onRowClick={(user) => navigate(`/users/${user.id}`, { state: { backSearch: window.location.search } })}
        rowHref={(user) => `/users/${user.id}`}
        rowClassName={(user) => user.deletedAt ? 'dt-row--disabled' : ''}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={{
          page,
          totalPages,
          total: data?.total,
          onPageChange: setPage,
        }}
        loading={isLoading && !isPlaceholderData}
        emptyMessage="No users"
        columnToggleKey={`admin-users-${role}`}
      />
      {pageSortToast && (
        <div className="users-sort-toast" role="status" onClick={() => setPageSortToast(false)}>
          Sorting <strong>Submissions</strong> within the current page only — not across all pages.
        </div>
      )}
    </>
  );
}

// ── Main page ───────────────────────────────────────────────────────────

type UserTab = 'student' | 'teacher' | 'admin';

export function Users() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: UserTab = tabParam === 'teacher' || tabParam === 'admin' ? tabParam : 'student';
  const setTab = (next: UserTab) =>
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (next === 'student') p.delete('tab'); else p.set('tab', next);
      return p;
    }, { replace: true });
  const [showDeleted, setShowDeleted] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const selectedTenantId = useSelectedTenantId();
  const tenantScope = selectedTenantId === ALL_TENANTS ? undefined : selectedTenantId;

  // Tenant column visibility: whoever sees more than one tenant's rows needs
  // it (the sidebar filter's audience), and only when not scoped — otherwise
  // every row carries the same tenant id.
  const me = authApi.getUser();
  const isSuperAdmin = me?.isSuperAdmin === true;
  const showTenant = operatesAcrossTenants(me) && tenantScope === undefined;

  // Invite is gated to the same authorities the backend accepts in
  // inviteTenantMember: super-admin (with a specific tenant chosen in the
  // sidebar TenantFilter) or tenant_admin (locked to their own tenant).
  // The Admins tab keeps its own invite/promote flow on TenantAdmins.
  const callerIsTenantAdmin = !!me && isTenantAdmin({ roles: me.roles, isSuperAdmin: me.isSuperAdmin });
  const inviteTenantId: string | null = isSuperAdmin
    ? (tenantScope ?? null)
    : (callerIsTenantAdmin ? (me?.tenantId ?? '0') : null);
  const showInviteButton = tab !== 'admin' && (isSuperAdmin || callerIsTenantAdmin);
  const canInvite = showInviteButton && inviteTenantId !== null;
  const inviteRole: 'student' | 'teacher' = tab === 'teacher' ? 'teacher' : 'student';
  const inviteBlockedHint = isSuperAdmin
    ? 'Pick a specific tenant in the sidebar filter to enable invite.'
    : '';

  // Teacher list for the student filter dropdown — scoped to the same tenant
  // so super-admin viewing one tenant doesn't see cross-tenant teachers.
  const { data: teacherData } = useQuery({
    queryKey: ['users', 'teacher', 'names', tenantScope],
    queryFn: () => adminApi.getUsers({ role: 'teacher', pageSize: 100, sortBy: 'name', sortOrder: 'asc', tenantId: tenantScope }),
  });
  const teacherOptions = (teacherData?.items ?? []).map(t => ({ id: t.id, name: t.name }));

  // Ring and the per-student teacher map are platform machinery; a customer's
  // admin sees neither. Same viewer test as the user detail page.
  const isPlatformViewer = !!me && (isSuperAdmin || me.tenantId === PUBLIC_TENANT_ID);
  const adminColumns = getAdminColumns(showTenant, isPlatformViewer);
  const teacherColumns = getTeacherColumns(showTenant, isPlatformViewer);
  /* Whose data is on screen — which is NOT the same question as who is looking.

     A super-admin's sidebar decides for them. A school picked there is a school.
     "All tenants" and the system `platform` tenant both mean "no school": the
     list is then the public tenant's students, and a super-admin's own login
     carries `tenantId: 'platform'`, which the sidebar defaults to — so treating
     `platform` as anything but public hid the Teachers column from exactly the
     person who most needs it. TenantFilter states this rule (`hasScopedTenant`)
     and this page did not apply it.

     Anyone else is server-locked to their own tenant, which `tenantScope` never
     carries — so a school's admin, for whom it is undefined, must NOT fall
     through to the public layout either. */
  const viewedTenantId = isSuperAdmin
    ? (hasScopedTenant(selectedTenantId) ? selectedTenantId : PUBLIC_TENANT_ID)
    : (me?.tenantId ?? PUBLIC_TENANT_ID);
  const showTeachers = viewedTenantId === PUBLIC_TENANT_ID;
  const studentColumns = getStudentColumns(showTenant, showTeachers);

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>Users</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
          <label className="show-deleted-toggle">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            Show deleted
          </label>
          {showInviteButton && (
            <Button
              onClick={() => setInviteOpen(true)}
              disabled={!canInvite}
              title={canInvite ? undefined : inviteBlockedHint}
            >
              + Invite {inviteRole}
            </Button>
          )}
        </div>
      </div>

      {canInvite && (
        <InviteUserModal
          isOpen={inviteOpen}
          tenantId={inviteTenantId!}
          role={inviteRole}
          onClose={() => setInviteOpen(false)}
        />
      )}

      <TabStrip
        ariaLabel="User roles"
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'student', label: 'Students' },
          { key: 'teacher', label: 'Teachers' },
          { key: 'admin', label: 'Admins' },
        ] as const}
      />

      {tab === 'student' && (
        <UserTabPanel
          role="student"
          columns={studentColumns}
          showDeleted={showDeleted}
          defaultSort={{ sortBy: 'lastActivityAt', sortOrder: 'desc' }}
          filters={{ ring: true, teacher: true, dummy: true }}
          teachers={teacherOptions}
        />
      )}
      {tab === 'teacher' && (
        <UserTabPanel
          role="teacher"
          columns={teacherColumns}
          showDeleted={showDeleted}
          defaultSort={{ sortBy: 'lastActivityAt', sortOrder: 'desc' }}
        />
      )}
      {tab === 'admin' && (
        <UserTabPanel
          role="admin"
          columns={adminColumns}
          showDeleted={showDeleted}
        />
      )}
    </div>
  );
}
